import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { gsap } from "gsap";
import { getShowAwardCounts } from "../../utils/api/helpers/show";
import spinner from "../../assets/ico/spinner.gif";

const GenreShowAwardsCarousel = forwardRef(
  ({ show, genreSlug, featured }, ref) => {
    const [showAwards, setShowAwards] = useState([]);
    const [loading, setLoading] = useState(true);
    const infoRef = useRef();
    const tweenRef = useRef();
    const indexRef = useRef(0);
    const stopRequested = useRef(false);

    const buildCompetitionInfo = (competitions = []) =>
      competitions
        .map((comp) => {
          const group = comp.group || [];
          const individual = comp.individual || [];
          const program = comp.program || [];

          const total = group.length + individual.length + program.length;
          const wins =
            group.filter((a) => a.winner === 1).length +
            individual.filter((a) => a.winner === 1).length +
            program.filter((a) => a.winner === 1).length;

          return {
            title: comp.name,
            total,
            wins,
            label:
              wins > 0
                ? `${wins} ${wins === 1 ? "Win" : "Wins"}`
                : `${total} ${total === 1 ? "Nomination" : "Nominations"}`,
          };
        })
        .sort((a, b) => b.wins - a.wins);

    const renderInfoItem = (item) => `
    <div class="genre-show-info-item-title">${item?.title}</div>
    <div class="genre-show-info-item-label ${item?.label.includes("Win") ? "genre-show-info-item-label-bold" : ""}">${item?.label}</div>
  `;

    // Fetch awards safely after parent is rendered
    useEffect(() => {
      if (!featured || !genreSlug || !show?.slug) return;

      let isMounted = true;

      // Wait a tiny bit to ensure parent is rendered
      const delay = setTimeout(async () => {
        try {
          const res = await getShowAwardCounts(genreSlug, show.slug);
          if (!isMounted) return;

          const competitions = Array.isArray(res?.data?.competitions)
            ? res.data.competitions
            : [];

          // console.log(res);

          setShowAwards(buildCompetitionInfo(competitions));
        } catch (err) {
          console.error("getShowAwards failed:", err);
          if (isMounted) setShowAwards([]);
        } finally {
          if (isMounted) setLoading(false);
        }
      }, 50);

      return () => {
        isMounted = false;
        clearTimeout(delay);
        if (tweenRef.current) {
          tweenRef.current.kill();
          tweenRef.current = null;
        }
      };
    }, [featured, genreSlug, show?.slug]);

    // Initialize first award display
    useEffect(() => {
      if (!infoRef.current || showAwards.length === 0) return;

      const item = infoRef.current.querySelector(".genre-show-info-item");
      if (!item) return;

      item.innerHTML = renderInfoItem(showAwards[0]);
      gsap.set(item, { y: 0, opacity: 1 });
      indexRef.current = 0;
    }, [showAwards]);

    const startAnimation = () => {
      if (!infoRef.current || showAwards.length <= 1) return;
      const item = infoRef.current.querySelector(".genre-show-info-item");
      if (!item) return;

      const animateMessage = () => {
        gsap.to(item, {
          y: -10,
          opacity: 0,
          duration: 0.25,
          ease: "power3.in",
          onComplete: () => {
            if (!item) return;

            indexRef.current = (indexRef.current + 1) % showAwards.length;
            item.innerHTML = renderInfoItem(showAwards[indexRef.current]);
            gsap.set(item, { y: 10, opacity: 0 });

            gsap.to(item, {
              y: 0,
              opacity: 1,
              duration: 0.25,
              ease: "power3.out",
              onComplete: () => {
                // Only schedule next cycle if not stopped
                if (!stopRequested.current) {
                  tweenRef.current = gsap.delayedCall(2, animateMessage);
                } else {
                  tweenRef.current = null; // prevent further looping
                }
              },
            });
          },
        });
      };

      // Reset stop flag
      stopRequested.current = false;
      animateMessage();
    };

    const stopAnimation = () => {
      // Set a flag so next delayedCall isn't scheduled
      stopRequested.current = true;

      // Don't kill current tween, let it finish
      if (tweenRef.current) {
        tweenRef.current.kill(); // cancel only the delayedCall
        tweenRef.current = null;
      }
    };

    useImperativeHandle(ref, () => ({
      startAnimation,
      stopAnimation,
    }));

    if (loading)
      return (
        <div className="genre-show-info" ref={infoRef} aria-live="polite">
          <div className="genre-show-info-inner">
            <div className="genre-show-info-loading d-flex align-items-center justify-content-center">
              <img
                src={spinner}
                alt={"Loading"}
                style={{ width: "32px", height: "32px" }}
              />
            </div>
          </div>
        </div>
      );
    if (!showAwards.length)
      return (
        <div className="genre-show-info" ref={infoRef} aria-live="polite">
          <div className="genre-show-info-inner">
            {show?.review_quote ? show?.review_quote : "For Your Consideration"}
          </div>
        </div>
      );

    return (
      <div className="genre-show-info" ref={infoRef} aria-live="polite">
        <div className="genre-show-info-inner">
          <div className="genre-show-info-item"></div>
        </div>
        <div
          className="genre-show-info-shadow genre-show-info-shadow-dark"
          aria-hidden="true"
        />
        <div
          className="genre-show-info-shadow genre-show-info-shadow-light"
          aria-hidden="true"
        />
      </div>
    );
  },
);

GenreShowAwardsCarousel.displayName = "GenreShowAwardsCarousel";
export default GenreShowAwardsCarousel;
